// =============================================================================
// FILE: packages/renderer/src/pages/LoginPage.tsx
// =============================================================================

import { useState, type FormEvent, useEffect, useRef, useCallback } from 'react';
import { Lock, Building2, Navigation, Server, Route, ShieldCheck, Crown, GraduationCap, Radio, Cpu } from 'lucide-react';
import { useAuthStore } from '../stores/auth.store.js';
import { call, isElectron } from '../lib/api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';
import { EntraDeviceCodeModal, type DeviceCodeChallenge } from '../components/auth/EntraDeviceCodeModal.js';
import { SpatialEstimator } from '../components/public/SpatialEstimator.js';
import { RackDrafter } from '../components/public/RackDrafter.js';
import { CivilEstimator } from '../components/public/CivilEstimator.js';
import { getBrand } from '@bim/shared/config/brand.config.js';
import { AccessRestricted } from '../components/auth/AccessRestricted.js';
import { ProjectConcierge } from '../components/public/ProjectConcierge.js';
import { RetailMenu } from '../components/public/RetailMenu.js';
import { CommunityPartners } from '../components/community/CommunityPartners.js';
import { AuctionLeaderboard } from '../components/public/AuctionLeaderboard';
import { FeaturedPartnersSection } from '../components/partners/FeaturedPartnersSection';
import { DataPacketJourneyButton } from '../components/public/DataPacketJourney';
import { LandingFooter } from '../components/public/LandingFooter';
import { GlobalOperationalMap } from '../components/public/GlobalOperationalMap';
import { R2Companion } from '../components/public/R2Companion';
import { MiniEcgMonitor } from '../components/branding/MiniEcgMonitor';
import { CapabilitiesPage } from './CapabilitiesPage';
import { GovernmentFinancingPage } from './GovernmentFinancingPage';
import { VerifyDocumentModal } from '../components/security/VerifyDocumentModal';
import { NewsletterModal } from '../components/marketing/NewsletterModal.js';
import { ClientPortal } from '../components/client/ClientPortal.js';
import { LogoMark } from '../components/branding/LogoMark.js';

// ── StarryNight Canvas ────────────────────────────────────────────────────────
function StarryNightCanvas(): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef    = useRef<number>(0);
  const t         = useRef(0);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    t.current += 0.003;
    const tc = t.current;

    ctx.clearRect(0, 0, W, H);

    // ── Deep space base ──
    const bg = ctx.createRadialGradient(W * 0.3, H * 0.35, 0, W * 0.3, H * 0.35, W * 0.85);
    bg.addColorStop(0, 'rgba(10,18,45,0.98)');
    bg.addColorStop(0.5, 'rgba(5,10,28,0.99)');
    bg.addColorStop(1, 'rgba(2,4,14,1)');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // ── Nebula clouds ──
    const nebulae: Array<[number, number, number, string]> = [
      [W * 0.2, H * 0.25, W * 0.35, 'rgba(37,100,168,0.045)'],
      [W * 0.6, H * 0.15, W * 0.3, 'rgba(55,40,120,0.04)'],
      [W * 0.15, H * 0.7, W * 0.4, 'rgba(20,80,100,0.035)'],
      [W * 0.75, H * 0.6, W * 0.25, 'rgba(37,60,100,0.05)'],
    ];
    for (const [nx, ny, nr, color] of nebulae) {
      const neb = ctx.createRadialGradient(nx, ny, 0, nx, ny, nr);
      neb.addColorStop(0, color);
      neb.addColorStop(1, 'transparent');
      ctx.fillStyle = neb;
      ctx.fillRect(0, 0, W, H);
    }

    // ── Van Gogh-style flowing currents ──
    const currents = [
      { cx: W*0.1, cy: H*0.2, r: W*0.38, a: 0.8, color: 'rgba(37,181,168,0.018)' },
      { cx: W*0.7, cy: H*0.1, r: W*0.42, a: 1.1, color: 'rgba(94,140,230,0.015)' },
      { cx: W*0.4, cy: H*0.8, r: W*0.35, a: 0.5, color: 'rgba(37,181,168,0.012)' },
    ];
    for (const cur of currents) {
      for (let i = 0; i < 4; i++) {
        const phase = tc * 0.4 + i * (Math.PI / 2);
        const rx = cur.cx + Math.cos(phase) * cur.r * cur.a;
        const ry = cur.cy + Math.sin(phase * 0.7) * cur.r * 0.5;
        ctx.beginPath();
        ctx.moveTo(cur.cx, cur.cy);
        ctx.bezierCurveTo(
          cur.cx + Math.cos(phase) * cur.r * 0.5,
          cur.cy + Math.sin(phase) * cur.r * 0.3,
          rx - 30, ry - 20, rx, ry
        );
        ctx.strokeStyle = cur.color;
        ctx.lineWidth = 1.5 + Math.sin(tc + i) * 0.5;
        ctx.stroke();
      }
    }

    // ── Stars ──
    const starSeed = [
      [0.12,0.08,1.8],[0.25,0.14,1.2],[0.38,0.06,2.1],[0.52,0.18,1.4],[0.67,0.09,1.9],[0.81,0.22,1.1],
      [0.06,0.31,1.5],[0.18,0.42,1.0],[0.31,0.29,2.3],[0.44,0.38,1.2],[0.58,0.26,1.7],[0.73,0.35,1.0],
      [0.87,0.41,1.4],[0.09,0.55,1.8],[0.22,0.62,1.1],[0.35,0.48,2.0],[0.49,0.57,1.3],[0.63,0.44,1.6],
      [0.77,0.58,1.0],[0.91,0.52,1.9],[0.04,0.72,1.2],[0.16,0.79,1.5],[0.29,0.68,1.8],[0.42,0.74,1.1],
      [0.56,0.67,2.2],[0.70,0.75,1.0],[0.84,0.69,1.4],[0.95,0.78,1.7],[0.08,0.88,1.3],[0.21,0.93,1.6],
      [0.34,0.85,2.0],[0.47,0.91,1.1],[0.61,0.82,1.5],[0.75,0.87,1.8],[0.89,0.92,1.2],[0.14,0.19,1.0],
      [0.57,0.33,1.3],[0.44,0.11,1.8],[0.78,0.47,1.0],[0.93,0.30,1.5],[0.03,0.47,2.0],[0.66,0.88,1.2],
      [0.19,0.05,1.4],[0.82,0.15,1.7],[0.37,0.95,1.0],[0.50,0.50,1.6],[0.71,0.22,1.3],[0.27,0.77,1.5],
      [0.90,0.64,1.1],[0.55,0.03,2.2],[0.11,0.66,1.4],[0.72,0.98,1.0],[0.46,0.23,1.7],[0.88,0.05,1.2],
      [0.33,0.56,1.9],[0.62,0.71,1.3],[0.17,0.38,1.0],[0.79,0.83,1.6],[0.41,0.45,1.8],[0.05,0.20,1.1],
    ];
    for (const [fx, fy, fr] of starSeed) {
      if (fx === undefined || fy === undefined) continue;
      const sx = fx * W, sy = fy * H, sr = fr as number;
      const twinkle = 0.55 + 0.45 * Math.sin(tc * 1.2 + sx * 0.01 + sy * 0.01);
      ctx.beginPath();
      ctx.arc(sx, sy, sr * twinkle, 0, Math.PI * 2);
      const brightness = Math.floor(180 + 75 * twinkle);
      ctx.fillStyle = `rgba(${brightness},${brightness + 20},255,${0.5 + 0.5 * twinkle})`;
      ctx.fill();
      // Soft glow for larger stars
      if (sr > 1.6) {
        const glow = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr * 4);
        glow.addColorStop(0, `rgba(200,220,255,${0.15 * twinkle})`);
        glow.addColorStop(1, 'transparent');
        ctx.fillStyle = glow;
        ctx.fillRect(sx - sr * 5, sy - sr * 5, sr * 10, sr * 10);
      }
    }

    // ── Network nodes ──
    const nodes: Array<{ x: number; y: number; r: number; phase: number }> = [
      { x: W*0.15, y: H*0.3,  r: 3.5, phase: 0.0 },
      { x: W*0.28, y: H*0.18, r: 2.5, phase: 1.2 },
      { x: W*0.42, y: H*0.35, r: 3.0, phase: 2.4 },
      { x: W*0.55, y: H*0.15, r: 2.0, phase: 0.8 },
      { x: W*0.68, y: H*0.42, r: 3.5, phase: 1.6 },
      { x: W*0.22, y: H*0.62, r: 2.5, phase: 3.0 },
      { x: W*0.38, y: H*0.72, r: 2.0, phase: 0.4 },
      { x: W*0.62, y: H*0.65, r: 3.0, phase: 2.0 },
      { x: W*0.78, y: H*0.28, r: 2.5, phase: 1.0 },
      { x: W*0.85, y: H*0.55, r: 2.0, phase: 2.8 },
    ];
    const nodeConnections = [[0,1],[1,2],[2,3],[3,4],[0,5],[5,6],[6,7],[7,4],[2,6],[1,8],[8,9],[4,9]];
    // Lines
    for (const [a, b] of nodeConnections) {
      if (a === undefined || b === undefined) continue;
      const na = nodes[a]!, nb = nodes[b]!;
      const pulse = 0.3 + 0.25 * Math.sin(tc * 1.5 + na.phase + nb.phase);
      ctx.beginPath();
      ctx.moveTo(na.x, na.y);
      ctx.lineTo(nb.x, nb.y);
      ctx.strokeStyle = `rgba(37,181,168,${pulse * 0.35})`;
      ctx.lineWidth = 0.8;
      ctx.stroke();
      // Animated data packet
      const progress = (tc * 0.3 + na.phase * 0.5) % 1;
      const px = na.x + (nb.x - na.x) * progress;
      const py = na.y + (nb.y - na.y) * progress;
      ctx.beginPath();
      ctx.arc(px, py, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(94,234,212,${0.6 + 0.4 * Math.sin(tc * 3)})`;
      ctx.fill();
    }
    // Nodes
    for (const n of nodes) {
      const pulse = 0.7 + 0.3 * Math.sin(tc * 1.2 + n.phase);
      // Outer ring
      ctx.beginPath();
      ctx.arc(n.x, n.y, n.r * 2.5 * pulse, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(37,181,168,${0.15 * pulse})`;
      ctx.lineWidth = 1;
      ctx.stroke();
      // Core
      ctx.beginPath();
      ctx.arc(n.x, n.y, n.r * pulse, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(37,181,168,${0.5 * pulse})`;
      ctx.fill();
    }

    // ── BIM grid horizon ──
    ctx.save();
    ctx.globalAlpha = 0.04 + 0.02 * Math.sin(tc * 0.5);
    const gridY = H * 0.78;
    for (let gx = 0; gx < W; gx += 48) {
      ctx.beginPath(); ctx.moveTo(gx, gridY); ctx.lineTo(gx, H);
      ctx.strokeStyle = '#25B5A8'; ctx.lineWidth = 0.5; ctx.stroke();
    }
    for (let gy = gridY; gy < H; gy += 28) {
      ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy);
      ctx.stroke();
    }
    ctx.restore();

    // ── Telemetry sweep line ──
    const sweepX = (tc * 0.12 % 1) * W;
    const sweep = ctx.createLinearGradient(sweepX - 40, 0, sweepX + 8, 0);
    sweep.addColorStop(0, 'transparent');
    sweep.addColorStop(0.7, 'rgba(37,181,168,0.04)');
    sweep.addColorStop(1, 'rgba(94,234,212,0.12)');
    ctx.fillStyle = sweep;
    ctx.fillRect(sweepX - 40, 0, 48, H);

    rafRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    rafRef.current = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(rafRef.current); ro.disconnect(); };
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.9 }}
      aria-hidden="true"
    />
  );
}

// ── Mission Control Card ──────────────────────────────────────────────────────
function MissionCard({
  icon, title, subtitle, accent, testId, onClick,
}: {
  icon: JSX.Element;
  title: string;
  subtitle: string;
  accent: string;
  testId: string;
  onClick: () => void;
}): JSX.Element {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        width: '100%', textAlign: 'left', padding: '12px 14px',
        background: hovered ? `${accent}12` : 'rgba(8,14,32,0.72)',
        border: `1px solid ${hovered ? accent : `${accent}35`}`,
        borderRadius: 10,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        boxShadow: hovered ? `0 0 22px ${accent}22, inset 0 1px 0 ${accent}18` : 'none',
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
        transition: 'all 0.22s cubic-bezier(0.34,1.56,0.64,1)',
        cursor: 'pointer',
      }}
    >
      <div style={{
        flexShrink: 0, width: 38, height: 38, borderRadius: 10,
        background: `${accent}18`,
        border: `1px solid ${accent}45`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: accent,
        boxShadow: hovered ? `0 0 14px ${accent}30` : 'none',
        transition: 'box-shadow 0.22s ease',
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#dce8f8', marginBottom: 2, letterSpacing: '0.01em' }}>
          {title}
        </div>
        <div style={{ fontSize: 10.5, color: 'rgba(148,172,210,0.65)', lineHeight: 1.35 }}>
          {subtitle}
        </div>
      </div>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: accent, opacity: hovered ? 1 : 0.4, transition: 'opacity 0.2s, transform 0.2s', transform: hovered ? 'translateX(3px)' : 'translateX(0)' }}>
        <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
  );
}

// ── Telemetry Ticker ──────────────────────────────────────────────────────────
function TelemetryTicker(): JSX.Element {
  const [vals, setVals] = useState({ latency: 4, uptime: 99.98, packets: 1842 });
  useEffect(() => {
    const iv = setInterval(() => {
      setVals(v => ({
        latency:  Math.max(2, Math.min(12, v.latency  + (Math.random() - 0.5) * 2)),
        uptime:   Math.min(100, v.uptime + 0.001),
        packets:  v.packets + Math.floor(Math.random() * 8),
      }));
    }, 1400);
    return () => clearInterval(iv);
  }, []);
  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
      {[
        { label: 'LATENCY', value: `${vals.latency.toFixed(1)}ms`, color: '#5eead4' },
        { label: 'UPTIME',  value: `${vals.uptime.toFixed(2)}%`,  color: '#4ade80' },
        { label: 'PACKETS', value: vals.packets.toLocaleString(), color: '#60a5fa' },
      ].map(({ label, value, color }) => (
        <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 8, fontFamily: 'monospace', letterSpacing: '0.15em', color: 'rgba(100,140,180,0.6)', textTransform: 'uppercase' }}>{label}</span>
          <span style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 700, color }}>{value}</span>
        </div>
      ))}
    </div>
  );
}

export function LoginPage(): JSX.Element {
  const brand = getBrand();
  const { login, loginWithEntra, setEntraSession, loading, error } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState<DeviceCodeChallenge | null>(null);
  const [entraEnabled, setEntraEnabled] = useState(false);
  const [showEstimator, setShowEstimator] = useState(false);
  const [showRackDrafter, setShowRackDrafter] = useState(false);
  const [showCivil, setShowCivil] = useState(false);
  const [showConcierge, setShowConcierge] = useState(false);
  const [showRetail, setShowRetail] = useState(false);
  const [showCapabilities, setShowCapabilities] = useState(false);
  const [showGovFinancing, setShowGovFinancing] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [showNews, setShowNews] = useState(false);
  const [showClientPortal, setShowClientPortal] = useState(false);

  useEffect(() => {
    if (window.api) {
      void call<undefined, { enabled: boolean }>(IPC.AUTH_ENTRA_STATUS)
        .then((r) => setEntraEnabled(Boolean(r.enabled)))
        .catch(() => setEntraEnabled(false));

      const offCode = window.api.on<DeviceCodeChallenge>(IPC.PUSH_ENTRA_DEVICE_CODE, (c) => setChallenge(c));
      const offDone = window.api.on<{ success: boolean; data?: Parameters<typeof setEntraSession>[0]; error?: { message: string } }>(
        IPC.AUTH_ENTRA_DEVICE_AWAIT,
        (result) => {
          setChallenge(null);
          if (result.success && result.data) setEntraSession(result.data);
        },
      );
      return () => { offCode(); offDone(); };
    }
    return undefined;
  }, [setEntraSession]);

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    void login(email, password);
  }

  const isBlocked = !!error && error.startsWith('Access Restricted');

  return (
    <div data-testid="login-page" className="relative min-h-screen w-screen bg-bg grid-bg" style={{ background: 'rgb(4,8,20)' }}>

      {/* 3D Environment Layer — architectural grid + atmosphere */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        {/* Grid floor perspective */}
        <div style={{
          position: 'absolute', bottom: 0, left: '-20%', right: '-20%', height: '45%',
          transform: 'perspective(500px) rotateX(62deg)',
          transformOrigin: 'bottom center',
          backgroundImage: 'linear-gradient(rgba(37,181,168,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(37,181,168,0.07) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          maskImage: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 65%)',
          WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 65%)',
        }} />
        {/* Ambient depth gradient */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse 90% 70% at 30% 40%, rgba(37,60,100,0.25) 0%, transparent 70%)',
        }} />
        {/* Horizon line */}
        <div style={{
          position: 'absolute', bottom: '43%', left: 0, right: 0, height: 1,
          background: 'linear-gradient(90deg, transparent 0%, rgba(37,181,168,0.2) 20%, rgba(94,234,212,0.35) 50%, rgba(37,181,168,0.2) 80%, transparent 100%)',
          filter: 'blur(1px)',
        }} />
      </div>

      {showCapabilities && (
        <div data-testid="public-capabilities-overlay" className="fixed inset-0 z-50 overflow-auto bg-bg">
          <button
            type="button"
            data-testid="public-capabilities-back"
            onClick={() => setShowCapabilities(false)}
            className="fixed top-5 left-5 z-[60] h-9 px-4 rounded-full border border-zinc-700 bg-zinc-900/80 text-zinc-200 text-[12px] hover:border-teal-400/60 transition flex items-center gap-2"
          >
            ← Back to Home
          </button>
          <div className="max-w-6xl mx-auto px-6 py-16">
            <CapabilitiesPage onNavigate={() => setShowCapabilities(false)} />
          </div>
        </div>
      )}
      {/* Client Login — top-right CTA; focuses the sign-in form */}
      <button
        type="button"
        data-testid="client-login-btn"
        onClick={() => {
          const el = document.querySelector<HTMLInputElement>('[data-testid="login-email"]');
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el?.focus();
        }}
        className="absolute top-5 right-40 z-30 h-9 px-4 rounded-full border border-zinc-700 bg-zinc-900/70 text-zinc-300 text-[12px] font-semibold tracking-wide hover:border-teal-400/60 transition flex items-center gap-2"
      >
        <Lock className="h-3.5 w-3.5" strokeWidth={1.75} />
        Staff Login
      </button>
      {/* VIP Managed Client Portal — invite-only tenant workspace */}
      <button
        type="button"
        data-testid="client-portal-btn"
        onClick={() => setShowClientPortal(true)}
        className="absolute top-5 right-6 z-30 h-9 px-4 rounded-full text-[12px] font-semibold tracking-wide transition flex items-center gap-2"
        style={{ background: 'linear-gradient(135deg,rgba(244,213,118,0.18),rgba(184,134,11,0.18))', border: '1px solid rgba(212,175,55,0.5)', color: '#f4d576' }}
      >
        <Crown className="h-3.5 w-3.5" strokeWidth={1.9} />
        Client Portal
      </button>
      <div className="min-h-[100svh] w-full flex flex-col lg:flex-row" style={{ position: 'relative', zIndex: 1 }}>
      {/* ── Left Cinematic Hero Panel ─────────────────────────────────────── */}
      <div className="flex-1 min-h-[100svh] hidden lg:grid grid-rows-[auto_1fr_auto] gap-3 xl:gap-4 px-8 py-6 xl:px-10 xl:py-7 2xl:px-12 2xl:py-9 border-r border-zinc-800/60"
        style={{ position: 'relative', overflow: 'hidden', background: 'rgba(3,6,18,0.92)' }}>

        {/* Starry Night animated background */}
        <StarryNightCanvas />

        {/* Content above canvas */}
        <div style={{ position: 'relative', zIndex: 2 }}>
          {/* Logo + Brand */}
          <div className="flex items-center gap-3">
            <LogoMark size={36} />
            <div className="leading-tight">
              <div data-testid="login-brand-product" className="text-sm font-semibold tracking-wide text-ui">{brand.family} · {brand.productName}</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{brand.tagline.split('.')[0]}</div>
            </div>
            <MiniEcgMonitor width={92} height={30} showReadout={false} onOpen={() => setShowCapabilities(true)} />
          </div>

          {/* Mission Control cards — upgraded from tiny text links */}
          <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, maxWidth: 820 }}>
            <MissionCard
              testId="public-academy-link"
              icon={<GraduationCap size={22} strokeWidth={1.75} />}
              title="R2 Academy — Free Learning Centre"
              subtitle="RCDD certification paths, BIM intelligence guides, and engineering training modules."
              accent="#25B5A8"
              onClick={() => { window.location.hash = 'academy'; window.location.reload(); }}
            />
            <MissionCard
              testId="login-subscribe-news"
              icon={<Radio size={22} strokeWidth={1.75} />}
              title="Subscribe to R2 News"
              subtitle="Live platform updates, engineering intelligence briefs, and release notes."
              accent="#60a5fa"
              onClick={() => setShowNews(true)}
            />
            <MissionCard
              testId="hero-journey-compact"
              icon={<Cpu size={22} strokeWidth={1.75} />}
              title="Data Packet Journey"
              subtitle="Visualize how a data packet travels through a BIM-modeled telecommunications infrastructure."
              accent="#a78bfa"
              onClick={() => { /* handled by DataPacketJourneyButton internally */ }}
            />
          </div>
        </div>

        {/* Hero copy */}
        <div style={{ position: 'relative', zIndex: 2, alignSelf: 'start', paddingTop: 'clamp(18px, 8vh, 110px)' }} className="grid xl:grid-cols-[minmax(420px,620px)_minmax(320px,520px)] gap-5 xl:gap-7 items-center">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-3">
              Enterprise 3D · BIM Intelligence · Offline-First
            </div>
            <h1 className="text-[2rem] xl:text-5xl font-semibold tracking-tight leading-[1.05]">
              R² Enterprise 3D: The Command Station for{' '}
              <span className="text-amber-300">RCDD-Certified Engineers</span>
            </h1>
            <p className="text-zinc-400 mt-3 text-[13px] leading-relaxed max-w-xl">
              3D BIM intelligence, asset registry, rack elevations, clash detection, and AI-powered design tools —
              all offline-first with enterprise-grade security and Microsoft Entra ID identity.
            </p>
            {/* Discount banner */}
            <div style={{
              marginTop: 12, padding: '8px 12px', borderRadius: 8,
              background: 'rgba(37,181,168,0.07)',
              border: '1px solid rgba(37,181,168,0.25)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>🏛</span>
              <span style={{ fontSize: 12, color: 'rgba(148,212,210,0.9)', lineHeight: 1.5 }}>
                <strong style={{ color: '#5eead4' }}>Save up to 60% off</strong> — Eligible organizations including government agencies, educational institutions, non-profits, and faith-based organizations may qualify.
              </span>
            </div>

            {/* Primary hero CTAs */}
            <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="hero-cta-row">
              <button
                type="button"
                data-testid="hero-launch-btn"
                onClick={() => {
                  const el = document.querySelector<HTMLInputElement>('[data-testid="login-email"]');
                  el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  el?.focus();
                }}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-[13px] font-bold transition-all hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg,#fcd34d,#f59e0b)', color: '#1a1405', border: '1px solid rgba(252,211,77,0.6)' }}
              >
                <Navigation className="h-4 w-4" /> Launch RCD² Platform
              </button>
              <DataPacketJourneyButton variant="hero" />
            </div>
          </div>

          {/* Global Operational Map */}
          <div style={{ position: 'relative', zIndex: 2 }} className="w-full max-h-[30vh] xl:max-h-[34vh] overflow-hidden rounded-xl border border-zinc-800/50">
            <GlobalOperationalMap />
          </div>
        </div>

        {/* Footer stats + telemetry */}
        <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <TelemetryTicker />
          <div className="flex items-baseline gap-6 text-[11px] text-zinc-500 font-mono">
            <span>34 tables</span>
            <span>·</span>
            <span>14 typed repositories</span>
            <span>·</span>
            <span>180+ event channels</span>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[430px] xl:w-[460px] flex items-center justify-center px-5 py-5 lg:px-7 lg:py-6 min-h-[100svh] overflow-y-auto">
        <div className="w-full max-w-sm lg:max-w-[360px]">
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">Sign in</div>
            <h2 className="text-2xl font-semibold tracking-tight">Welcome back, operator.</h2>
            <p className="text-[12px] text-zinc-500 mt-1">
              {isElectron() ? 'Local + Microsoft Entra ID accepted.' : 'Web preview — using mock backend.'}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-3" data-testid="login-form" autoComplete="off">
            {/* Stealth Vault decoys — absorb aggressive browser/password-manager autofill
                so the real founder credentials are never tracked, saved, or offered. */}
            <input type="text" name="username" autoComplete="username" tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />
            <input type="password" name="password" autoComplete="new-password" tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">Work Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                name="rcd2_ident_field"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-10 bg-zinc-900/60 border border-zinc-800 rounded-sm px-3 text-[13px] focus:outline-none focus:border-amber-300/60 transition"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">Password</label>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type="password"
                  required
                  name="rcd2_secure_field"
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-10 bg-zinc-900/60 border border-zinc-800 rounded-sm px-3 pr-9 text-[13px] focus:outline-none focus:border-amber-300/60 transition"
                />
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-600" strokeWidth={1.75} />
              </div>
            </div>

            {error && !isBlocked && (
              <div data-testid="login-error" className="px-3 py-2 rounded-sm border border-rose-900/60 bg-rose-950/40 text-rose-300 text-[12px]">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-10 rounded-sm bg-amber-300 text-zinc-900 font-semibold text-[13px] hover:bg-amber-200 disabled:opacity-60 transition flex items-center justify-center gap-2"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>

            <button
              type="button"
              data-testid="login-azure"
              disabled={!entraEnabled && isElectron()}
              onClick={() => void loginWithEntra()}
              className="w-full h-10 rounded-sm border border-zinc-700 text-zinc-200 text-[13px] hover:border-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              title={entraEnabled || !isElectron() ? '' : 'AZURE_TENANT_ID / AZURE_CLIENT_ID not configured'}
            >
              <Building2 className="h-3.5 w-3.5" strokeWidth={1.75} />
              Continue with Microsoft Entra ID
              {!entraEnabled && isElectron() && (
                <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider">· disabled</span>
              )}
            </button>
          </form>

          <div className="mt-4 pt-4 border-t border-zinc-800/70">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Visiting client? No login needed</div>
            <button
              type="button"
              data-testid="open-spatial-estimator"
              onClick={() => setShowEstimator(true)}
              className="w-full h-9 rounded-sm border text-[12px] font-semibold flex items-center justify-center gap-2 transition"
              style={{ borderColor: 'rgba(37,181,168,0.5)', background: 'rgba(37,181,168,0.10)', color: '#5eead4' }}
            >
              <Navigation className="h-4 w-4" strokeWidth={1.9} />
              Try the Spatial Estimator — instant cabling cost
            </button>
            <button
              type="button"
              data-testid="open-rack-drafter"
              onClick={() => { window.location.hash = 'rack-maker-preview'; }}
              className="w-full h-9 mt-1.5 rounded-sm border text-[12px] font-semibold flex items-center justify-center gap-2 transition"
              style={{ borderColor: 'rgba(252,211,77,0.5)', background: 'rgba(252,211,77,0.08)', color: '#fcd34d' }}
            >
              <Server className="h-4 w-4" strokeWidth={1.9} />
              Preview On-Demand Rack Drafter deliverable
            </button>
            <button
              type="button"
              data-testid="open-civil-estimator"
              onClick={() => setShowCivil(true)}
              className="w-full h-9 mt-1.5 rounded-sm border text-[12px] font-semibold flex items-center justify-center gap-2 transition"
              style={{ borderColor: 'rgba(129,140,248,0.5)', background: 'rgba(129,140,248,0.08)', color: '#a5b4fc' }}
            >
              <Route className="h-4 w-4" strokeWidth={1.9} />
              Preview Civil Ductbank & Manhole Estimator
            </button>
            <button
              type="button"
              data-testid="open-project-concierge"
              onClick={() => setShowConcierge(true)}
              className="w-full h-9 mt-1.5 rounded-sm text-[12px] font-semibold flex items-center justify-center gap-2 transition"
              style={{ background: 'linear-gradient(135deg,#25B5A8,#0f766e)', color: '#04110f' }}
            >
              <Navigation className="h-4 w-4" strokeWidth={1.9} />
              Solve My Project — instant Startup Package
            </button>
            <button
              type="button"
              data-testid="open-retail-menu"
              onClick={() => setShowRetail(true)}
              className="w-full h-9 mt-1.5 rounded-sm border text-[12px] font-semibold flex items-center justify-center gap-2 transition"
              style={{ borderColor: 'rgba(226,232,240,0.3)', background: 'rgba(226,232,240,0.05)', color: '#e2e8f0' }}
            >
              À-La-Carte Services — Free Preview
            </button>
            <button
              type="button"
              data-testid="open-gov-financing"
              onClick={() => setShowGovFinancing(true)}
              className="w-full h-9 mt-1.5 rounded-sm border text-[12px] font-semibold flex items-center justify-center gap-2 transition"
              style={{ borderColor: 'rgba(252,211,77,0.4)', background: 'rgba(252,211,77,0.06)', color: '#fcd34d' }}
            >
              🏛️ Government &amp; Community Deployment — Financing
            </button>
            <button
              type="button"
              data-testid="open-verify-document"
              onClick={() => setShowVerify(true)}
              className="w-full h-9 mt-2 text-[11px] font-medium flex items-center justify-center gap-1.5 text-zinc-400 hover:text-teal-300 transition"
            >
              🛡️ Verify an R2 Document
            </button>
            <p className="text-[10.5px] text-zinc-600 mt-2 leading-relaxed">
              Enter two points and R² estimates your run in seconds. Start a 100-day trial to unlock the itemized PDF.
            </p>
          </div>

          <p className="text-[10.5px] text-zinc-600 mt-4 leading-relaxed font-mono">
            By signing in you agree to platform RBAC and audit-trail policies.
            All mutations are timestamped and persisted to <span className="text-zinc-400">audit_trail</span>.
          </p>
          <p className="text-[10.5px] text-zinc-600 mt-3 leading-relaxed" data-testid="eligibility-note">
            Access to the RCD² Engine is subject to eligibility verification. We reserve the right to restrict
            access to specific organizations to protect the integrity of the platform.
          </p>
          <div className="mt-4 flex items-center gap-4 text-[11px]">
            <a data-testid="need-help-link" href="mailto:rcd2engine@gmail.com?subject=RCD2%20Engine%20-%20Support%20Inquiry"
              className="text-zinc-400 hover:text-amber-300 transition">Need help?</a>
            <span className="text-zinc-700">·</span>
            <a data-testid="contact-founder-footer" href="mailto:rcd2engine@gmail.com?subject=RCD2%20Engine%20-%20Contact%20Founder"
              className="text-zinc-400 hover:text-amber-300 transition">Contact Founder</a>
          </div>

          {/* Security & Privacy shield */}
          <div data-testid="security-shield" className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(63,63,70,0.4)' }}>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {['ISO Certified', 'SOC 2 Compliant', '256-bit SSL Encrypted'].map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wide"
                  style={{ background: 'rgba(37,181,168,0.1)', border: '1px solid rgba(37,181,168,0.3)', color: '#5eead4' }}>
                  <ShieldCheck className="h-3 w-3" /> {b}
                </span>
              ))}
            </div>
            <p className="text-[10.5px] text-zinc-600 leading-relaxed">
              Your engineering data is protected by enterprise-grade isolation and encryption.
              RCD² Engine never uses your proprietary designs to train public AI models.
            </p>
          </div>

          <CommunityPartners />
        </div>
      </div>
      </div>

      {/* ── Featured Partners + Strategic Investor Allocation Program (after hero) ── */}
      <FeaturedPartnersSection />

      {/* ── Global Master Auction — public anonymized leaderboard (below hero) ── */}
      <AuctionLeaderboard />

      {/* ── Social hub footer ── */}
      <LandingFooter />

      {isBlocked && <AccessRestricted message={error} onBack={() => useAuthStore.setState({ error: null })} />}
      <EntraDeviceCodeModal challenge={challenge} onClose={() => setChallenge(null)} />      {showEstimator && <SpatialEstimator onClose={() => setShowEstimator(false)} />}
      {showRackDrafter && <RackDrafter onClose={() => setShowRackDrafter(false)} />}
      {showCivil && <CivilEstimator onClose={() => setShowCivil(false)} />}
      {showConcierge && <ProjectConcierge onClose={() => setShowConcierge(false)} />}
      {showRetail && (
        <RetailMenu
          onClose={() => setShowRetail(false)}
          onOpenTool={(id) => {
            setShowRetail(false);
            if (id === 'rack-drafter') window.location.hash = 'rack-maker-preview';
            else if (id === 'spatial-estimator') setShowEstimator(true);
          }}
        />
      )}
      {/* Persistent R2 Companion — dockable Emmy chat that survives reloads */}
      <R2Companion />
      <NewsletterModal open={showNews} onClose={() => setShowNews(false)} />
      {showGovFinancing && <GovernmentFinancingPage onClose={() => setShowGovFinancing(false)} />}
      {showVerify && <VerifyDocumentModal onClose={() => setShowVerify(false)} />}
      {showClientPortal && <ClientPortal onExit={() => setShowClientPortal(false)} />}
    </div>
  );
}
